import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { GeminiHeader } from "@/components/GeminiHeader";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { Skeleton } from "@/components/ui/skeleton";
import * as LucideIcons from "lucide-react";

interface HelpCategory {
  id: string;
  name: string;
  name_id: string;
  icon: string;
}

interface HelpArticle {
  id: string;
  slug: string;
  title: string;
  title_id: string;
  summary: string;
  summary_id: string;
  sort_order: number;
  article_type?: "steps" | "article" | null;
}

const HelpCategory = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [category, setCategory] = useState<HelpCategory | null>(null);
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (categoryId) {
      loadCategoryContent();
    }
  }, [categoryId]);

  const loadCategoryContent = async () => {
    try {
      // Load category
      const { data: categoryData } = await supabase
        .from("help_categories")
        .select("*")
        .eq("id", categoryId)
        .maybeSingle();
      
      if (categoryData) {
        setCategory(categoryData);
      }

      // Load articles for this category
      const { data: articlesData } = await supabase
        .from("help_articles")
        .select("*")
        .eq("category_id", categoryId)
        .eq("is_active", true)
        .order("sort_order");
      
      if (articlesData) {
        const normalized = (articlesData as any[]).map((a) => ({
          ...a,
          article_type: a.article_type || "steps",
        })) as HelpArticle[];
        setArticles(normalized);
      }
    } catch (error) {
      console.error("Error loading category content:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getIcon = (iconName: string) => {
    const Icon = (LucideIcons as any)[iconName] || LucideIcons.HelpCircle;
    return <Icon className="h-5 w-5" />;
  };

  const categoryTitle = category 
    ? (language === "id" ? category.name_id : category.name)
    : (language === "id" ? "Kategori" : "Category");

  const explanationLabel = language === "id" ? "Penjelasan" : "Explanation";
  const guideLabel = language === "id" ? "Panduan" : "Guide";

  const explanationArticles = articles.filter((a) => a.article_type === "article");
  const guideArticles = articles.filter((a) => a.article_type !== "article");

  return (
    <div className="min-h-screen bg-background">
      <GeminiHeader title={categoryTitle} showBack useHistoryBack />
      
      <div className="p-4 pb-24 max-w-lg mx-auto">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-20 rounded-2xl" />
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {language === "id" ? "Belum ada artikel" : "No articles yet"}
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Explanation first */}
            <div className="space-y-3">
              <div className="flex items-center justify-between px-1">
                <h2 className="text-sm font-semibold text-foreground">
                  {explanationLabel}
                </h2>
                <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                  <LucideIcons.BookOpen className="h-4 w-4" />
                  <span>{explanationArticles.length}</span>
                </div>
              </div>
              <div className="space-y-3">
                {explanationArticles.map((article, index) => {
                  const isArticle = true;

                  return (
                    <motion.button
                      key={article.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => navigate(`/help/${article.slug}`)}
                      className="w-full text-left p-4 rounded-2xl bg-card border border-border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary">
                          {category && getIcon(category.icon)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <h3 className="font-semibold">
                              {language === "id" ? article.title_id : article.title}
                            </h3>
                            <div className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${isArticle ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                              {isArticle ? (
                                <LucideIcons.BookOpen className="h-3.5 w-3.5" />
                              ) : (
                                <LucideIcons.ListChecks className="h-3.5 w-3.5" />
                              )}
                              {isArticle ? explanationLabel : guideLabel}
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                            {language === "id" ? article.summary_id : article.summary}
                          </p>
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Guides */}
            <div className="space-y-3">
              <div className="flex items-center justify-between px-1">
                <h2 className="text-sm font-semibold text-foreground">
                  {guideLabel}
                </h2>
                <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                  <LucideIcons.ListChecks className="h-4 w-4" />
                  <span>{guideArticles.length}</span>
                </div>
              </div>
              <div className="space-y-3">
                {guideArticles.map((article, index) => {
                  const isArticle = false;

                  return (
                    <motion.button
                      key={article.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => navigate(`/help/${article.slug}`)}
                      className="w-full text-left p-4 rounded-2xl bg-card border border-border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary">
                          {category && getIcon(category.icon)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <h3 className="font-semibold">
                              {language === "id" ? article.title_id : article.title}
                            </h3>
                            <div className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${isArticle ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                              {isArticle ? (
                                <LucideIcons.BookOpen className="h-3.5 w-3.5" />
                              ) : (
                                <LucideIcons.ListChecks className="h-3.5 w-3.5" />
                              )}
                              {isArticle ? explanationLabel : guideLabel}
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                            {language === "id" ? article.summary_id : article.summary}
                          </p>
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpCategory;
